import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  Radio, ShieldCheck, Sparkles, CheckCircle2, XCircle, BellOff, Loader2,
} from "lucide-react";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { SignalsTabs } from "@/components/signals/SignalsTabs";
import { SignalEvidenceDialog } from "@/components/signals/SignalEvidenceDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SIGNALS } from "@/constants/testIds";
import { SEVERITY } from "@/lib/alerts";
import { SOURCE_LABEL, STATUS, STATUS_FILTERS, canReviewRole } from "@/lib/signals";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

function rel(ts) {
  if (!ts) return "—";
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return "—"; }
}

function SignalRow({ s, canReview, onOpen, onAct, acting }) {
  const sev = SEVERITY[s.severity] || SEVERITY.low;
  const st = STATUS[s.status] || STATUS.pending;
  const png = s.evidence_frozen?.chart_png_b64;
  return (
    <div data-testid={SIGNALS.row}
      className="grid grid-cols-12 items-center gap-3 border-b border-border/40 px-4 py-3 transition-colors last:border-0 hover:bg-muted/30">
      <button onClick={() => onOpen(s)} data-testid={SIGNALS.rowOpen} className="col-span-12 flex items-center gap-3 text-left sm:col-span-4">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${sev.dot}`} />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{s.name}</div>
          <div className="truncate text-xs text-muted-foreground">{s.summary || "—"}</div>
        </div>
      </button>

      <div className="col-span-4 sm:col-span-1">
        <Badge variant="outline" className="text-[10px] text-accent border-accent/30">{SOURCE_LABEL[s.source] || s.source}</Badge>
      </div>

      <div className="col-span-4 sm:col-span-2">
        <Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
      </div>

      <button onClick={() => onOpen(s)} className="col-span-4 hidden text-xs text-muted-foreground sm:block sm:col-span-2">
        {rel(s.detected_at)}
      </button>

      <button onClick={() => onOpen(s)} className="col-span-12 hidden justify-start sm:flex sm:col-span-1">
        {png ? (
          <img src={`data:image/png;base64,${png}`} alt="evidence" className="h-9 w-16 rounded border border-border/50 object-cover" />
        ) : <span className="text-[10px] text-muted-foreground">no chart</span>}
      </button>

      <div className="col-span-12 flex justify-end gap-1 sm:col-span-2">
        {canReview && s.status === "pending" ? (
          <>
            <Button data-testid={SIGNALS.silenceBtn} variant="ghost" size="icon" title="Silence" disabled={acting}
              onClick={() => onAct(s.id, "silence")} className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground">
              <BellOff className="h-4 w-4" />
            </Button>
            <Button data-testid={SIGNALS.rejectBtn} variant="ghost" size="icon" title="Reject" disabled={acting}
              onClick={() => onAct(s.id, "reject")} className="h-8 w-8 rounded-full text-red-400 hover:bg-red-500/10">
              <XCircle className="h-4 w-4" />
            </Button>
            <Button data-testid={SIGNALS.approveBtn} variant="ghost" size="icon" title="Approve" disabled={acting}
              onClick={() => onAct(s.id, "approve")} className="h-8 w-8 rounded-full text-emerald-400 hover:bg-emerald-500/10">
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            </Button>
          </>
        ) : s.registry_id ? (
          <Link to={`/signals/registry/${s.registry_id}`} className="inline-flex items-center gap-1 text-[11px] text-emerald-400 hover:underline">
            <ShieldCheck className="h-3.5 w-3.5" /> record
          </Link>
        ) : <span className="text-[11px] text-muted-foreground">{s.reviewer_name || ""}</span>}
      </div>
    </div>
  );
}

export default function SignalsQueue() {
  const { active } = useWorkspace();
  const [signals, setSignals] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [acting, setActing] = useState(false);

  const canReview = canReviewRole(active?.role);

  const load = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    try {
      const params = { workspace_id: active.id };
      if (status !== "all") params.status = status;
      const { data } = await api.get("/signals", { params });
      setSignals(data);
    } finally {
      setLoading(false);
    }
  }, [active, status]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, verb) => {
    setActing(true);
    try {
      await api.post(`/signals/${id}/${verb}`);
      toast.success(verb === "approve" ? "Approved — written to registry" : `Signal ${verb}ed`);
      await load();
    } catch (e) {
      toast.error("Action failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setActing(false);
    }
  };

  const pendingCount = useMemo(() => signals.filter((s) => s.status === "pending").length, [signals]);

  return (
    <div data-testid={SIGNALS.queueRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-7xl px-6 py-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Signals</h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Breaches someone reviewed become governed signals with provenance — a compliance-grade record of “did we know about this?”
            </p>
          </div>
          <div className="w-full sm:w-44">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid={SIGNALS.statusFilter} className="bg-card/60"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <SignalsTabs />

        <div className="mt-4 rounded-xl border border-border/60 bg-card/30">
          <div className="flex items-center justify-between border-b border-border/40 px-4 py-3 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            <span>{status === "all" ? "All signals" : STATUS[status]?.label} </span>
            {status === "pending" && <span>{pendingCount} awaiting review</span>}
          </div>
          {loading ? (
            <div className="px-4 py-16 text-center text-sm text-muted-foreground">Loading…</div>
          ) : signals.length ? (
            signals.map((s) => (
              <SignalRow key={s.id} s={s} canReview={canReview} acting={acting}
                onOpen={(sig) => setOpenId(sig.id)} onAct={act} />
            ))
          ) : (
            <div data-testid={SIGNALS.queueEmpty} className="px-4 py-20 text-center">
              <Sparkles className="mx-auto h-8 w-8 text-accent" />
              <h2 className="mt-4 text-lg font-semibold">Nothing to review</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Breached <Link to="/alerts" className="text-accent hover:underline">alerts</Link> land here automatically, or promote a finding from <Link to="/chat" className="text-accent hover:underline">Ask</Link>.
              </p>
            </div>
          )}
        </div>
      </main>

      <SignalEvidenceDialog
        signalId={openId}
        open={!!openId}
        onOpenChange={(o) => !o && setOpenId(null)}
        canReview={canReview}
        onChanged={load}
      />
    </div>
  );
}
